import { css } from 'aphrodite';
import { addWeeks, subWeeks } from 'date-fns';
import {
	parseAsArrayOf,
	parseAsBoolean,
	parseAsIsoDate,
	parseAsString,
	parseAsStringEnum,
	useQueryState,
	useQueryStates,
} from 'nuqs';
import * as React from 'react';
import { DemoStatusFilter, IAccountTag, IUser } from '../../../extViewmodels';
import { getDisplayName } from '../../../models/NameUtils';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { addUserToUserQueryCache } from '../../../queries';
import { useLeadPhoneCallOutcomesQuery } from '../../../queries/Lead/useLeadPhoneCallOutcomesQuery';
import { useTagSearchQuery } from '../../../queries/Tag/useTagSearchQuery';
import { useUserAutocompleteQuery } from '../../../queries/User/useUserAutocompleteQuery';
import { Button } from '../../../web/components-v2/Button';
import { ButtonGroup } from '../../../web/components-v2/ButtonGroup';
import { Calendar } from '../../../web/components-v2/Calendar';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemCheckboxIndicator,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { XIconNew } from '../../../web/components/svgs/icons/XIcon';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserCombobox,
	UserComboboxAvatarItem,
	UserComboboxStatus,
} from '../../../web/components/UserCombobox';
import { bs } from '../../../web/styles/styles';
import { FieldKey } from '../../viewModels/form';
import {
	ADD_ON_OPTIONS,
	DEAL_STAGE_OPTIONS,
	DEMO_STATUS_OPTIONS,
	FilterOption,
	INBOUND_CHANNEL_OPTIONS,
	INDUSTRY_OPTIONS,
	IS_COLD_DEMO_OPTIONS,
	LEAD_SOURCE_OPTIONS,
	LOST_REASON_OPTIONS,
	OVERBOOK_OPTIONS,
} from './constants';
import { styleSheet } from './styles';

interface FiltersProps {
	onSearch: () => void;
	isSearching?: boolean;
	searchDisabled?: boolean;
}

export const Filters = ({ onSearch, isSearching, searchDisabled }: FiltersProps) => {
	const id = React.useId();
	const userSession = useUserSession();
	const leadPhoneCallOutcomesQuery = useLeadPhoneCallOutcomesQuery();
	const [activeUserId, setActiveUserId] = useQueryState('userId', parseAsString);
	const [dateRange, setDateRange] = useQueryStates({
		from: parseAsIsoDate,
		to: parseAsIsoDate,
	});
	const [demoStatus, setDemoStatus] = useQueryState(
		'demoStatus',
		parseAsStringEnum<DemoStatusFilter>(Object.values(DemoStatusFilter)).withDefault(DemoStatusFilter.DemosToConfirm)
	);
	const [outcomeName, setOutcomeName] = useQueryState(
		'outcomeName',
		parseAsStringEnum<FieldKey>(Object.values(FieldKey)).withDefault(FieldKey.Unknown)
	);
	const [managerUserId, setManagerUserId] = useQueryState('managerUserId', parseAsString);
	const [demoPerformerUserId, setDemoPerformerUserId] = useQueryState(
		'demoPerformerUserId',
		parseAsArrayOf(parseAsString)
	);
	const [companyId, setCompanyId] = useQueryState('companyId', parseAsString);

	// Past Demos tab filter state (multi-select arrays)
	const [dealStatus, setDealStatus] = useQueryState('dealStatus', parseAsArrayOf(parseAsString));
	const [leadSource, setLeadSource] = useQueryState('leadSource', parseAsArrayOf(parseAsString));
	const [addOn, setAddOn] = useQueryState('addOn', parseAsArrayOf(parseAsString));
	const [inboundChannel, setInboundChannel] = useQueryState('inboundChannel', parseAsArrayOf(parseAsString));
	const [lostReasonCategories, setLostReasonCategories] = useQueryState(
		'lostReasonCategories',
		parseAsArrayOf(parseAsString)
	);
	const [industry, setIndustry] = useQueryState('industry', parseAsArrayOf(parseAsString));
	const [tag, setTag] = useQueryState('tag', parseAsArrayOf(parseAsString));
	const [isColdDemo, setIsColdDemo] = useQueryState('isColdDemo', parseAsBoolean);
	const [overbook, setOverbook] = useQueryState('overbook', parseAsBoolean);
	const [followUpDateRange, setFollowUpDateRange] = useQueryStates({
		followUpDateStart: parseAsIsoDate,
		followUpDateEnd: parseAsIsoDate,
	});
	const [targetCloseDateRange, setTargetCloseDateRange] = useQueryStates({
		targetCloseDateStart: parseAsIsoDate,
		targetCloseDateEnd: parseAsIsoDate,
	});

	// Local state for dynamic multi-select objects (closer owner users and tags)
	const [closerOwnerUsers, setCloserOwnerUsers] = React.useState<IUser[]>([]);
	const [closerSearchValue, setCloserSearchValue] = React.useState('');
	const [selectedTags, setSelectedTags] = React.useState<IAccountTag[]>([]);
	const [tagSearchValue, setTagSearchValue] = React.useState('');

	// Derive active tab from demoStatus
	const isSalesCoffeeAccount = userSession?.account?.isLevitateSalesCoffeeAccount;
	const isPastDemosTab = demoStatus === DemoStatusFilter.PastDemos;

	// Dynamic search queries for closer owner and tag
	const closerAutocompleteQuery = useUserAutocompleteQuery({
		fragment: closerSearchValue,
		enabled: Boolean(closerSearchValue),
		domain: isSalesCoffeeAccount ? 'levitateapp.com' : undefined,
	});
	const tagSearchQuery = useTagSearchQuery({
		query: tagSearchValue,
		enabled: Boolean(tagSearchValue),
	});

	// Track if we've done the initial SDR prepopulation (to avoid re-populating after user clears)
	const hasInitializedSdrFilter = React.useRef(false);

	// Redirect non-sales-coffee accounts away from Past Demos tab
	React.useEffect(() => {
		if (isPastDemosTab && !isSalesCoffeeAccount) {
			setDemoStatus(DemoStatusFilter.DemosToConfirm);
		}
	}, [isPastDemosTab, isSalesCoffeeAccount, setDemoStatus]);

	// Prepopulate SDR filter with current user on upcoming demos tab (only on initial load)
	// Skip prepopulation when companyId is present (e.g. navigating from "view demo recordings" on a lead record)
	React.useEffect(() => {
		const currentUserId = userSession?.user?.id;
		if (!isPastDemosTab && !hasInitializedSdrFilter.current && !activeUserId && !companyId && currentUserId) {
			setActiveUserId(currentUserId);
			hasInitializedSdrFilter.current = true;
		}
	}, [isPastDemosTab, activeUserId, companyId, userSession?.user?.id, setActiveUserId]);

	// Shared function to clear all filter state
	const clearAllFilters = React.useCallback(() => {
		setActiveUserId(null);
		setManagerUserId(null);
		setDemoPerformerUserId(null);
		setDateRange({ from: null, to: null });
		setOutcomeName(null);
		setCompanyId(null);
		// Clear Past Demos tab filters
		setDealStatus(null);
		setLeadSource(null);
		setAddOn(null);
		setInboundChannel(null);
		setLostReasonCategories(null);
		setIndustry(null);
		setTag(null);
		setIsColdDemo(null);
		setOverbook(null);
		setFollowUpDateRange({ followUpDateStart: null, followUpDateEnd: null });
		setTargetCloseDateRange({ targetCloseDateStart: null, targetCloseDateEnd: null });
		// Clear local object states
		setCloserOwnerUsers([]);
		setCloserSearchValue('');
		setSelectedTags([]);
		setTagSearchValue('');
	}, [
		setActiveUserId,
		setManagerUserId,
		setDemoPerformerUserId,
		setDateRange,
		setOutcomeName,
		setCompanyId,
		setDealStatus,
		setLeadSource,
		setAddOn,
		setInboundChannel,
		setLostReasonCategories,
		setIndustry,
		setTag,
		setIsColdDemo,
		setOverbook,
		setFollowUpDateRange,
		setTargetCloseDateRange,
	]);

	// Tab switch handler - clears all filters when switching tabs
	const handleTabChange = React.useCallback(
		(tab: 'upcoming' | 'past') => {
			clearAllFilters();
			setDemoStatus(tab === 'past' ? DemoStatusFilter.PastDemos : DemoStatusFilter.DemosToConfirm);
		},
		[clearAllFilters, setDemoStatus]
	);

	// Keyboard handler for tab navigation
	const handleTabKeyDown = React.useCallback(
		(e: React.KeyboardEvent, tab: 'upcoming' | 'past') => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				handleTabChange(tab);
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
				e.preventDefault();
				handleTabChange(tab === 'upcoming' ? 'past' : 'upcoming');
			}
		},
		[handleTabChange]
	);

	const handleUserChange = React.useCallback(
		(user: IUser) => {
			setActiveUserId(user?.id ?? null);
			// Clear manager selection since they're mutually exclusive
			if (user?.id) {
				addUserToUserQueryCache({ id: user.id, user });
				setManagerUserId(null);
			}
		},
		[setActiveUserId, setManagerUserId]
	);

	const handleCloserOwnerChange = React.useCallback(
		(users: IUser[]) => {
			setCloserOwnerUsers(users);
			setDemoPerformerUserId(users.length > 0 ? users.map(u => u.id) : null);
		},
		[setDemoPerformerUserId]
	);

	const handleTagChange = React.useCallback(
		(tags: IAccountTag[]) => {
			setSelectedTags(tags);
			setTag(tags.length > 0 ? tags.map(t => t.name) : null);
		},
		[setTag]
	);

	const handleManagerChange = React.useCallback(
		(user: IUser) => {
			setManagerUserId(user?.id ?? null);
			// Clear user selection since they're mutually exclusive
			if (user?.id) {
				setActiveUserId(null);
			}
		},
		[setManagerUserId, setActiveUserId]
	);

	const handleOutcomeChange = React.useCallback(
		(newOutcomeName: FieldKey) => {
			setOutcomeName(newOutcomeName);
		},
		[setOutcomeName]
	);

	const handleClearAllFilters = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			clearAllFilters();
			// Stay on current tab but reset demo status dropdown on Upcoming tab
			if (isPastDemosTab) {
				// Stay on Past Demos tab
				setDemoStatus(DemoStatusFilter.PastDemos);
			} else {
				// Reset to default on Upcoming tab
				setDemoStatus(DemoStatusFilter.DemosToConfirm);
			}
		},
		[clearAllFilters, isPastDemosTab, setDemoStatus]
	);

	const handleDemoStatusChange = React.useCallback(
		(status: DemoStatusFilter) => {
			if (status === DemoStatusFilter.DemosToConfirm) {
				setOutcomeName(null);
			}
			if (status === DemoStatusFilter.DemosToReschedule) {
				setDateRange({ from: subWeeks(new Date(), 1), to: new Date() });
			}
			if (status === DemoStatusFilter.UpcomingDemos) {
				const currentDate = new Date();
				setDateRange({ from: currentDate, to: addWeeks(currentDate, 1) });
			}
			setDemoStatus(status);
		},
		[setOutcomeName, setDateRange, setDemoStatus]
	);

	// Check if any filters are active (for Upcoming tab, check if demo status changed from default)
	const hasActiveFilters =
		companyId ||
		activeUserId ||
		managerUserId ||
		(demoPerformerUserId?.length ?? 0) > 0 ||
		dateRange.from ||
		dateRange.to ||
		(!isPastDemosTab && demoStatus !== DemoStatusFilter.DemosToConfirm) ||
		outcomeName !== FieldKey.Unknown ||
		// Past Demos tab filters
		(dealStatus?.length ?? 0) > 0 ||
		(leadSource?.length ?? 0) > 0 ||
		(addOn?.length ?? 0) > 0 ||
		(inboundChannel?.length ?? 0) > 0 ||
		(lostReasonCategories?.length ?? 0) > 0 ||
		(industry?.length ?? 0) > 0 ||
		(tag?.length ?? 0) > 0 ||
		isColdDemo !== null ||
		overbook !== null ||
		followUpDateRange.followUpDateStart ||
		followUpDateRange.followUpDateEnd ||
		targetCloseDateRange.targetCloseDateStart ||
		targetCloseDateRange.targetCloseDateEnd;

	const outcomeItems: { label: string; value: FieldKey }[] = [{ label: 'All outcome types', value: FieldKey.Unknown }];
	leadPhoneCallOutcomesQuery.data?.forEach(outcome => {
		outcomeItems.push({
			label: outcome.label,
			value: outcome.name,
		});
	});

	return (
		<>
			{/* Tab header */}
			<div className={css(styleSheet.tabsContainer)} role='tablist' aria-label='Demo filters'>
				<div
					className={css(styleSheet.tab, !isPastDemosTab && styleSheet.tabActive)}
					onClick={() => handleTabChange('upcoming')}
					onKeyDown={e => handleTabKeyDown(e, 'upcoming')}
					role='tab'
					aria-selected={!isPastDemosTab}
					tabIndex={!isPastDemosTab ? 0 : -1}
				>
					Upcoming Demos / Demos to Reschedule
				</div>
				{isSalesCoffeeAccount && (
					<div
						className={css(styleSheet.tab, isPastDemosTab && styleSheet.tabActive)}
						onClick={() => handleTabChange('past')}
						onKeyDown={e => handleTabKeyDown(e, 'past')}
						role='tab'
						aria-selected={isPastDemosTab}
						tabIndex={isPastDemosTab ? 0 : -1}
					>
						Past Demos
					</div>
				)}
			</div>

			{/* Filter grid */}
			<div className={css(styleSheet.filter)}>
				<UserCombobox id={activeUserId} key={`sdr-${activeUserId}`} onValueChange={handleUserChange}>
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor={`${id}-sdr-combobox`} className={css(styleSheet.label)}>
							SDR
						</label>
						<ComboboxInput placeholder='Filter by SDR' id={`${id}-sdr-combobox`} showClear />
					</div>
					<ComboboxContent>
						<UserComboboxStatus />
						<ComboboxList>
							{(user: IUser) => <UserComboboxAvatarItem key={`sdr-item-${user.id}`} user={user} />}
						</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
				{!isPastDemosTab && (
					<ManagerCombobox id={managerUserId} key={`managers-${managerUserId}`} onValueChange={handleManagerChange}>
						<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
							<label htmlFor={`${id}-manager-combobox`} className={css(bs.textXs, bs.fontBold)}>
								Manager
							</label>
							<ComboboxInput placeholder='Filter by manager' id={`${id}-manager-combobox`} showClear />
						</div>
						<ComboboxContent>
							<ManagerComboboxStatus />
							<ComboboxList>
								{(user: IUser) => <UserComboboxAvatarItem key={`manager-item-${user.id}`} user={user} />}
							</ComboboxList>
						</ComboboxContent>
					</ManagerCombobox>
				)}
				{/* Closer owner - multi-select with chips */}
				<Combobox
					multiple
					items={closerAutocompleteQuery.data?.values ?? []}
					value={closerOwnerUsers}
					onValueChange={(value: unknown) => handleCloserOwnerChange(value as IUser[])}
					onInputValueChange={setCloserSearchValue}
					inputValue={closerSearchValue}
					filter={null}
					itemToStringLabel={(item: unknown) => getDisplayName(item as IUser)}
					isItemEqualToValue={(a: unknown, b: unknown) => (a as IUser).id === (b as IUser).id}
				>
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor={`${id}-closer-combobox`} className={css(styleSheet.label)}>
							Closer owner
						</label>
						<ComboboxChips>
							{closerOwnerUsers.map(user => (
								<ComboboxChip key={user.id}>
									{getDisplayName(user)}
									<ComboboxChipRemove />
								</ComboboxChip>
							))}
							<ComboboxChipsInput placeholder='Filter by closer owner' id={`${id}-closer-combobox`} />
						</ComboboxChips>
					</div>
					<ComboboxContent>
						<ComboboxEmpty>{closerSearchValue ? 'No users found.' : 'Start typing to search...'}</ComboboxEmpty>
						<ComboboxList>
							{(user: IUser) => (
								<ComboboxItem key={user.id} value={user}>
									<ComboboxItemCheckboxIndicator />
									{getDisplayName(user)}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
				<Popover>
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor='dates' className={css(styleSheet.label)}>
							Demo Date Range
						</label>
						<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
							<PopoverTrigger asChild>
								<Button
									variant='outline'
									id='dates'
									styles={[styleSheet.dateButton, dateRange.from || dateRange.to ? styleSheet.dateButtonActive : null]}
								>
									{dateRange?.from && dateRange?.to
										? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
										: 'Filter by Demo Date'}
									{!dateRange?.from && !dateRange?.to ? <ChevronDown /> : null}
								</Button>
							</PopoverTrigger>
							{dateRange.from || dateRange.to ? (
								<Button variant='outline' onClick={() => setDateRange({ from: null, to: null })}>
									<XIconNew />
								</Button>
							) : null}
						</ButtonGroup>
					</div>
					<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
						<Calendar
							mode='range'
							selected={dateRange}
							numberOfMonths={2}
							timeZone='UTC'
							onSelect={r => setDateRange(r)}
						/>
					</PopoverContent>
				</Popover>
				{!isPastDemosTab && (
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor='demo-status' className={css(styleSheet.label)}>
							Demo Status
						</label>
						<Select value={demoStatus} onValueChange={handleDemoStatusChange} items={DEMO_STATUS_OPTIONS}>
							<SelectTrigger id='demo-status' styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{DEMO_STATUS_OPTIONS.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				{/* Past Demos tab multi-select filters */}
				{isPastDemosTab && (
					<MultiSelectFilter
						id={`${id}-deal-stage`}
						label='Deal Stage'
						placeholder='Filter by deal stage'
						options={DEAL_STAGE_OPTIONS}
						value={dealStatus}
						onValueChange={items => setDealStatus(items.length > 0 ? items.map(i => i.value) : null)}
					/>
				)}
				{isPastDemosTab && (
					<MultiSelectFilter
						id={`${id}-lead-source`}
						label='Lead Source'
						placeholder='Filter by lead source'
						options={LEAD_SOURCE_OPTIONS}
						value={leadSource}
						onValueChange={items => setLeadSource(items.length > 0 ? items.map(i => i.value) : null)}
					/>
				)}
				{isPastDemosTab && (
					<MultiSelectFilter
						id={`${id}-industry`}
						label='Industry'
						placeholder='Filter by industry'
						options={INDUSTRY_OPTIONS}
						value={industry}
						onValueChange={items => setIndustry(items.length > 0 ? items.map(i => i.value) : null)}
					/>
				)}
				{isPastDemosTab && (
					<MultiSelectFilter
						id={`${id}-add-on`}
						label='Add-On'
						placeholder='Filter by add-on'
						options={ADD_ON_OPTIONS}
						value={addOn}
						onValueChange={items => setAddOn(items.length > 0 ? items.map(i => i.value) : null)}
					/>
				)}
				{isPastDemosTab && (
					<MultiSelectFilter
						id={`${id}-inbound-channel`}
						label='Inbound Channel'
						placeholder='Filter by inbound channel'
						options={INBOUND_CHANNEL_OPTIONS}
						value={inboundChannel}
						onValueChange={items => setInboundChannel(items.length > 0 ? items.map(i => i.value) : null)}
					/>
				)}
				{isPastDemosTab && (
					<MultiSelectFilter
						id={`${id}-lost-reason`}
						label='Lost Reason'
						placeholder='Filter by lost reason'
						options={LOST_REASON_OPTIONS}
						value={lostReasonCategories}
						onValueChange={items => setLostReasonCategories(items.length > 0 ? items.map(i => i.value) : null)}
					/>
				)}
				{isPastDemosTab && (
					<Combobox
						items={IS_COLD_DEMO_OPTIONS}
						value={IS_COLD_DEMO_OPTIONS.find(opt => opt.value === isColdDemo) ?? null}
						onValueChange={item => setIsColdDemo((item as FilterOption<boolean>)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption<boolean>).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-is-cold-demo`} className={css(styleSheet.label)}>
								Is Cold Demo
							</label>
							<ComboboxInput placeholder='Filter by cold demo' id={`${id}-is-cold-demo`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: boolean }) => (
									<ComboboxItem key={item.label} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Combobox
						items={OVERBOOK_OPTIONS}
						value={OVERBOOK_OPTIONS.find(opt => opt.value === overbook) ?? null}
						onValueChange={item => setOverbook((item as FilterOption<boolean>)?.value ?? null)}
						itemToStringLabel={item => (item as FilterOption<boolean>).label}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-overbook`} className={css(styleSheet.label)}>
								Overbook
							</label>
							<ComboboxInput placeholder='Filter by overbook' id={`${id}-overbook`} showClear />
						</div>
						<ComboboxContent>
							<ComboboxEmpty>No results found.</ComboboxEmpty>
							<ComboboxList>
								{(item: { label: string; value: boolean }) => (
									<ComboboxItem key={item.label} value={item}>
										<ComboboxItemRadioIndicator />
										{item.label}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{isPastDemosTab && (
					<Popover>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor='follow-up-dates' className={css(styleSheet.label)}>
								Follow-Up Date Range
							</label>
							<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
								<PopoverTrigger asChild>
									<Button
										variant='outline'
										id='follow-up-dates'
										styles={[
											styleSheet.dateButton,
											followUpDateRange.followUpDateStart || followUpDateRange.followUpDateEnd
												? styleSheet.dateButtonActive
												: null,
										]}
									>
										{followUpDateRange?.followUpDateStart && followUpDateRange?.followUpDateEnd
											? `${followUpDateRange.followUpDateStart.toLocaleDateString()} - ${followUpDateRange.followUpDateEnd.toLocaleDateString()}`
											: 'Filter by Follow-Up Date'}
										{!followUpDateRange.followUpDateStart && !followUpDateRange.followUpDateEnd ? (
											<ChevronDown />
										) : null}
									</Button>
								</PopoverTrigger>
								{followUpDateRange.followUpDateStart || followUpDateRange.followUpDateEnd ? (
									<Button
										variant='outline'
										onClick={() => setFollowUpDateRange({ followUpDateStart: null, followUpDateEnd: null })}
									>
										<XIconNew />
									</Button>
								) : null}
							</ButtonGroup>
						</div>
						<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
							<Calendar
								mode='range'
								selected={{
									from: followUpDateRange.followUpDateStart,
									to: followUpDateRange.followUpDateEnd,
								}}
								numberOfMonths={2}
								timeZone='UTC'
								onSelect={r =>
									setFollowUpDateRange({ followUpDateStart: r?.from ?? null, followUpDateEnd: r?.to ?? null })
								}
							/>
						</PopoverContent>
					</Popover>
				)}
				{isPastDemosTab && (
					<Popover>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor='target-close-dates' className={css(styleSheet.label)}>
								Target Close Date
							</label>
							<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
								<PopoverTrigger asChild>
									<Button
										variant='outline'
										id='target-close-dates'
										styles={[
											styleSheet.dateButton,
											targetCloseDateRange.targetCloseDateStart || targetCloseDateRange.targetCloseDateEnd
												? styleSheet.dateButtonActive
												: null,
										]}
									>
										{targetCloseDateRange?.targetCloseDateStart && targetCloseDateRange?.targetCloseDateEnd
											? `${targetCloseDateRange.targetCloseDateStart.toLocaleDateString()} - ${targetCloseDateRange.targetCloseDateEnd.toLocaleDateString()}`
											: 'Filter by Target Close Date'}
										{!targetCloseDateRange.targetCloseDateStart && !targetCloseDateRange.targetCloseDateEnd ? (
											<ChevronDown />
										) : null}
									</Button>
								</PopoverTrigger>
								{targetCloseDateRange.targetCloseDateStart || targetCloseDateRange.targetCloseDateEnd ? (
									<Button
										variant='outline'
										onClick={() => setTargetCloseDateRange({ targetCloseDateStart: null, targetCloseDateEnd: null })}
									>
										<XIconNew />
									</Button>
								) : null}
							</ButtonGroup>
						</div>
						<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
							<Calendar
								mode='range'
								selected={{
									from: targetCloseDateRange.targetCloseDateStart,
									to: targetCloseDateRange.targetCloseDateEnd,
								}}
								numberOfMonths={2}
								timeZone='UTC'
								onSelect={r =>
									setTargetCloseDateRange({ targetCloseDateStart: r?.from ?? null, targetCloseDateEnd: r?.to ?? null })
								}
							/>
						</PopoverContent>
					</Popover>
				)}
				{/* Tag - multi-select with search and chips */}
				{isPastDemosTab && (
					<Combobox
						multiple
						items={tagSearchQuery.data ?? []}
						value={selectedTags}
						onValueChange={(value: unknown) => handleTagChange(value as IAccountTag[])}
						onInputValueChange={setTagSearchValue}
						inputValue={tagSearchValue}
						filter={null}
						itemToStringLabel={(item: unknown) => (item as IAccountTag).name}
						isItemEqualToValue={(a: unknown, b: unknown) => (a as IAccountTag).name === (b as IAccountTag).name}
					>
						<div className={css(styleSheet.filterGroup)}>
							<label htmlFor={`${id}-tag`} className={css(styleSheet.label)}>
								Tag
							</label>
							<ComboboxChips>
								{selectedTags.map(tagItem => (
									<ComboboxChip key={tagItem.name}>
										{tagItem.name}
										<ComboboxChipRemove />
									</ComboboxChip>
								))}
								<ComboboxChipsInput placeholder='Filter by tag' id={`${id}-tag`} />
							</ComboboxChips>
						</div>
						<ComboboxContent>
							<ComboboxEmpty>{tagSearchValue ? 'No tags found.' : 'Start typing to search...'}</ComboboxEmpty>
							<ComboboxList>
								{(tagItem: IAccountTag) => (
									<ComboboxItem key={tagItem.name} value={tagItem}>
										<ComboboxItemCheckboxIndicator />
										{tagItem.name}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				)}
				{!isPastDemosTab && (
					<div className={css(styleSheet.filterGroup)}>
						<label htmlFor={`${id}-outcome-select`} className={css(styleSheet.label)}>
							Outcome
						</label>
						<Select
							value={outcomeName}
							onValueChange={handleOutcomeChange}
							disabled={demoStatus !== DemoStatusFilter.DemosToConfirm}
							items={outcomeItems}
						>
							<SelectTrigger id={`${id}-outcome-select`} styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{outcomeItems.map(outcomeItem => (
									<SelectItem key={`${id}-outcome-${outcomeItem.value}`} value={outcomeItem.value}>
										{outcomeItem.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			</div>
			{/* Action buttons on their own line, right-aligned */}
			<div className={css(styleSheet.actionRow)}>
				{isPastDemosTab && (
					<>
						{searchDisabled && (
							<span className={css(bs.textGray600, bs.textSm)}>Please select a date range to search.</span>
						)}
						<Button variant='primary' onClick={onSearch} disabled={isSearching || searchDisabled}>
							{isSearching ? 'Searching...' : 'Search'}
						</Button>
					</>
				)}
				{hasActiveFilters && (
					<Button styles={[styleSheet.clearFiltersButton]} variant='secondary' onClick={handleClearAllFilters}>
						Clear all filters
					</Button>
				)}
			</div>
		</>
	);
};

/** Reusable multi-select filter for static option lists */
function MultiSelectFilter({
	id,
	label,
	placeholder,
	options,
	value,
	onValueChange,
}: {
	id: string;
	label: string;
	placeholder: string;
	options: FilterOption[];
	value: string[] | null;
	onValueChange: (items: FilterOption[]) => void;
}) {
	const selectedItems = options.filter(opt => value?.includes(opt.value));
	return (
		<Combobox
			multiple
			items={options}
			value={selectedItems}
			onValueChange={(items: unknown) => onValueChange(items as FilterOption[])}
			itemToStringLabel={(item: unknown) => (item as FilterOption).label}
			isItemEqualToValue={(a: unknown, b: unknown) => (a as FilterOption).value === (b as FilterOption).value}
		>
			<div className={css(styleSheet.filterGroup)}>
				<label htmlFor={id} className={css(styleSheet.label)}>
					{label}
				</label>
				<ComboboxChips>
					{selectedItems.map(item => (
						<ComboboxChip key={item.value}>
							{item.label}
							<ComboboxChipRemove />
						</ComboboxChip>
					))}
					<ComboboxChipsInput placeholder={placeholder} id={id} styles={[styleSheet.chipsInputFullRow]} />
				</ComboboxChips>
			</div>
			<ComboboxContent>
				<ComboboxEmpty>No results found.</ComboboxEmpty>
				<ComboboxList>
					{(item: FilterOption) => (
						<ComboboxItem key={item.value} value={item}>
							<ComboboxItemCheckboxIndicator />
							{item.label}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
}
